import { Box, DialogContent, Modal } from "@mui/material"
import { SxProps } from "@mui/system"
import {
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useRef } from "react"

import { DGText } from "../../../components/common/DGText"
import { Footer } from "../../../components/common/Footer"
import { useModalDismiss } from "../../../hooks/use-modal-dismiss"
import { useModalScroll } from "../../../hooks/use-modal-scroll"
import { appStore } from "../../../store/AppStore"
import { comb, s } from "../../../utilities/common"
import { parseMarkdown } from "../../../utilities/parse-markdown"
import { theme, TI } from "../../../utilities/theme"

type Props = {
  title: string
  content: string
  returnFocusKey?: string
  open: comb<boolean>
  style?: SxProps
}

export const InfoModal = ({ open, ...rest }: {} & Props) => {
  return open.val ? <Screen open={open} {...rest} /> : null
}

const Screen = ({ open, title, content, returnFocusKey, style }: Props) => {
  const setSpinner = appStore((state) => state.setSpinner)
  const callback = () => {
    if (returnFocusKey) setFocus(returnFocusKey)
    open.set(false)
  }
  useModalDismiss(() => callback())

  const scrollRef = useRef<HTMLElement>(null)

  useModalScroll(scrollRef)

  const { focusSelf } = useFocusable({
    isFocusBoundary: true,
  })

  useEffect(() => {
    focusSelf()
    setSpinner("out")
  }, [])

  const { ref } = useFocusable({ isFocusBoundary: true })

  const pageStyle = `.container {
      text-align: start;
      color: white;
      width: ${s(1160)}px;
    }
    h4 {
      font-weight: 400;
      font-size: ${s(34)}px;
      line-height: ${s(46)}px;
      letter-spacing: ${s(0.25)}px;
    }
    h3 {
      font-weight: 400;
      font-size: ${s(34)}px;
      line-height: ${s(46)}px;
      letter-spacing: ${s(0.25)}px;
    }
    h2 {
      font-weight: 400;
      font-size: ${s(34)}px;
      line-height: ${s(46)}px;
      letter-spacing: ${s(0.25)}px;
    }
    h1 {
      font-weight: 400;
      font-size: ${s(34)}px;
      line-height: ${s(46)}px;
      letter-spacing: ${s(0.25)}px;
    }
    p {
      font-weight: 400;
      font-size: ${s(30)}px;
      line-height: ${s(40)}px;
      letter-spacing: ${s(0.25)}px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: ${s(60)}px;
    }
    ul {
      font-weight: 400;
      font-size: ${s(30)}px;
      line-height: ${s(40)}px;
      letter-spacing: ${s(0.25)}px;
      color: rgba(255, 255, 255, 0.7);
    }
    ol {
      font-weight: 400;
      font-size: ${s(30)}px;
      line-height: ${s(40)}px;
      letter-spacing: ${s(0.25)}px;
      color: rgba(255, 255, 255, 0.7);
    }
    a {
      color: rgba(255, 255, 255, 1);
      text-decoration: none;
    }
    strong {
      color: white;
    }`

  return (
    <Modal
      open={open.val}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: "#1A2436",
        "& > *": { transition: "none !important" },
        ...style,
      }}
    >
      <DialogContent
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: theme.palette.primary.variant,
          position: "absolute",
          overflowY: "visible",
        }}
      >
        <Box ref={scrollRef} style={{ transition: "transform 0.25s linear" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              willChange: "transform",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: s("1160px"),
                mt: s("184px"),
              }}
            >
              {title && (
                <DGText
                  tx={TI.H05tv}
                  sx={{
                    color: theme.palette.white.main,
                    mb: s("8px"),
                  }}
                >
                  {title}
                </DGText>
              )}
            </Box>
            <style>{pageStyle}</style>
            <div
              ref={ref}
              tabIndex={0}
              className={"container"}
              dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
            />
            <Footer />
          </Box>
        </Box>
      </DialogContent>
    </Modal>
  )
}
